import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, BookOpen, Star, ExternalLink, RotateCcw, ChevronDown, ChevronUp, Clock, Award, Share2, Check, Bookmark, BookmarkCheck, CheckCircle, BookCheck, Menu, X, Moon, Sun } from 'lucide-react';
import ReactGA from "react-ga4";
import { BOOKS } from './booksData';

// Replace with the G- ID you found earlier
ReactGA.initialize("G-JHFBNZBZMC");

// Genre mapping from Q1 answers to book categories (includes sub-genres)
const GENRE_MAP = {
  "Science Fiction": [
    "Science Fiction", "Dystopian", "Space Opera", "Cyberpunk", "Hard SF",
    "Hard Science Fiction", "Post-Apocalyptic", "Apocalyptic", "Robots",
    "Military SF", "Invasion"
  ],
  "Fantasy": [
    "Fantasy", "Epic Fantasy", "Epic", "Urban Fantasy", "Paranormal",
    "Magical Realism", "Fairy Tale Retelling", "Grimdark", "Arthurian",
    "Mythology", "Supernatural", "Historical Fantasy", "Political Fantasy",
    "Young Adult", "Adventure"
  ],
  "Thriller/Mystery": [
    "Thriller", "Mystery", "Psychological", "Crime", "Espionage", "Noir",
    "Legal", "Conspiracy", "Conspiratorial", "True Crime", "Cozy Mystery",
    "Horror", "Gothic", "Vampires", "Action"
  ],
  "Contemporary Fiction/Literature": [
    "Contemporary Fiction", "Literary Fiction", "Contemporary", "Literary",
    "Coming of Age", "Coming-of-Age", "Drama", "Family Saga", "Social Issues",
    "Social Commentary", "Multicultural", "LGBTQ+", "LGBTQ", "Dark Academia",
    "New Adult", "Feminist", "Humor", "Satire", "Dark Comedy", "Comedy",
    "Absurdist Fiction", "Experimental", "Short Stories", "Novella"
  ],
  "Historical Fiction (novels set in the past)": [
    "Historical Fiction", "War", "World War I", "World War II", "Military",
    "Victorian", "Tudor", "Medieval", "Colonial Literature", "Regency",
    "War Fiction", "Western"
  ],
  "History (non-fiction history books)": [
    "History", "American History", "Military History", "Roman History",
    "Ancient History"
  ],
  "Classic Literature (timeless literary works)": [
    "Classic", "Classics", "Classic Literature", "Russian Literature",
    "French Literature", "American Literature", "Japanese Literature",
    "Irish", "Modernist", "Postmodern", "Lost Generation", "Southern Gothic",
    "Philosophical Fiction", "Tragedy", "African American Literature",
    "Harlem Renaissance", "Poetry"
  ],
  "Non-Fiction (Self-Help/Wellness)": [
    "Self-Help", "Wellness", "Psychology", "Mental Health", "Spirituality",
    "Health", "Personal Development", "Productivity", "Stoicism",
    "Relationships", "Communication", "Parenting"
  ],
  "Non-Fiction (Memoir/Biography/Business)": [
    "Memoir", "Biography", "Autobiography", "Business", "Finance",
    "Personal Finance", "Economics", "Strategy", "Nonfiction", "Non-Fiction",
    "Science", "Sociology", "Technology", "Politics", "Political",
    "Political Philosophy", "Philosophy", "Essays", "Nature", "Sports",
    "Entertainment", "Music", "Cooking", "Food", "Pop Culture", "Linguistics",
    "Evolution", "Inspiration", "Graphic Novel"
  ],
  "Romance": [
    "Romance", "Dark Romance", "Paranormal Romance"
  ]
};

// StarRating component with partial star support
const StarRating = ({ rating, size = 'w-5 h-5', color = 'yellow' }) => {
  const fullStars = Math.floor(rating);
  const partialFill = rating - fullStars; // e.g., 0.5 for 4.5 rating
  const emptyStars = 5 - fullStars - (partialFill > 0 ? 1 : 0);

  const fillColor = color === 'yellow' ? 'text-yellow-500 fill-yellow-500' : 'text-orange-500 fill-orange-500';
  const emptyColor = 'text-gray-300';

  return (
    <div className="flex">
      {/* Full stars */}
      {[...Array(fullStars)].map((_, i) => (
        <Star key={`full-${i}`} className={`${size} ${fillColor}`} />
      ))}

      {/* Partial star (if any) */}
      {partialFill > 0 && (
        <div className="relative" style={{ width: size.includes('w-4') ? '16px' : '20px', height: size.includes('h-4') ? '16px' : '20px' }}>
          {/* Empty star background */}
          <Star className={`${size} ${emptyColor} absolute top-0 left-0`} />
          {/* Filled star with clip */}
          <div
            className="absolute top-0 left-0 overflow-hidden"
            style={{ width: `${partialFill * 100}%` }}
          >
            <Star className={`${size} ${fillColor}`} />
          </div>
        </div>
      )}

      {/* Empty stars */}
      {[...Array(emptyStars)].map((_, i) => (
        <Star key={`empty-${i}`} className={`${size} ${emptyColor}`} />
      ))}
    </div>
  );
};

// Quiz questions - Q1 (genre), Q2 (length), Q3 (era) are hard filters, rest are scoring
const QUESTIONS = [
  {
    id: 1,
    question: "What genre calls to you right now?",
    subtitle: "Pick the one that speaks loudest",
    weight: 3,
    answers: [
      { text: "Science Fiction", scores: { scifi: 5 } },
      { text: "Fantasy", scores: { fantasy: 5 } },
      { text: "Thriller/Mystery", scores: { thriller: 4, mystery: 4 } },
      { text: "Contemporary Fiction/Literature", scores: { contemporary: 5, literary: 3, modern: 5 } },
      { text: "Historical Fiction (novels set in the past)", scores: { historical: 5 } },
      { text: "History (non-fiction history books)", scores: { history: 5, nonfiction: 3 } },
      { text: "Classic Literature (timeless literary works)", scores: { classic: 5, literary: 3 } },
      { text: "Non-Fiction (Self-Help/Wellness)", scores: { nonfiction: 5, mind: 3, selfhelp: 5 } },
      { text: "Non-Fiction (Memoir/Biography/Business)", scores: { nonfiction: 4, memoir: 4, modern: 3 } },
      { text: "Romance", scores: { heart: 5, emotional: 3, romance: 5 } }
    ]
  },
  {
    id: 2,
    question: "Ideal book length?",
    subtitle: "How much time do you want to invest?",
    weight: 2,
    answers: [
      { text: "Quick read (under 250 pages)", scores: { fast: 4 }, pageRange: { min: 0, max: 250 } },
      { text: "Standard length (250-500 pages)", scores: { medium: 3 }, pageRange: { min: 250, max: 500 } },
      { text: "Epic journey (500+ pages)", scores: { slow: 4, atmosphere: 3 }, pageRange: { min: 500, max: 99999 } },
      { text: "Length doesn't matter", scores: {}, pageRange: null }
    ]
  },
  {
    id: 3,
    question: "Which era speaks to you?",
    subtitle: "Fresh off the press or timeless classic?",
    weight: 2,
    answers: [
      { text: "Brand new (last 5 years)", scores: { modern: 5 }, yearRange: { min: 2021, max: 2030 } },
      { text: "Modern (2000-2020)", scores: { modern: 3 }, yearRange: { min: 2000, max: 2020 } },
      { text: "20th century classics (1900-1999)", scores: { classic: 3 }, yearRange: { min: 1900, max: 1999 } },
      { text: "Timeless classics (pre-1900)", scores: { classic: 5 }, yearRange: { min: 0, max: 1899 } },
      { text: "Era doesn't matter", scores: {}, yearRange: null }
    ]
  },
  {
    id: 4,
    question: "What's most important in a book?",
    subtitle: "The heart of what you're looking for",
    weight: 2.5,
    answers: [
      { text: "Deep, complex characters", scores: { character: 5 } },
      { text: "Gripping plot and twists", scores: { plot: 5 } },
      { text: "Beautiful, literary prose", scores: { literary: 5 } },
      { text: "Immersive world-building", scores: { atmosphere: 5, fantasy: 2, scifi: 2 } },
      { text: "Learn something new", scores: { nonfiction: 5, mind: 4, memoir: 3 } }
    ]
  },
  {
    id: 5,
    question: "What pacing do you crave?",
    subtitle: "How do you want the story to unfold?",
    weight: 2,
    answers: [
      { text: "Fast-paced page-turner", scores: { fast: 5 } },
      { text: "Steady and immersive", scores: { medium: 5 } },
      { text: "Slow burn, richly detailed", scores: { slow: 5 } },
      { text: "Doesn't matter if it's good", scores: {} }
    ]
  },
  {
    id: 6,
    question: "How do you want to feel?",
    subtitle: "The emotional journey",
    weight: 2,
    answers: [
      { text: "Uplifted and hopeful", scores: { optimistic: 5 } },
      { text: "Thoughtful and realistic", scores: { emotional: 3, literary: 2 } },
      { text: "Dark and intense", scores: { dark: 4, thriller: 2 } },
      { text: "Warm and heartfelt", scores: { heart: 5, emotional: 3 } }
    ]
  },
  {
    id: 7,
    question: "What's your commitment level?",
    subtitle: "One and done, or ready for more?",
    weight: 1.5,
    answers: [
      { text: "Standalone - complete story", scores: { standalone: 5 } },
      { text: "Series - I want more!", scores: { series: 5 } },
      { text: "Either works for me", scores: {} }
    ]
  },
  {
    id: 8,
    question: "How do you want to be moved?",
    subtitle: "Intellectual vs emotional connection",
    weight: 2,
    answers: [
      { text: "Make me think deeply", scores: { mind: 5, nonfiction: 2 } },
      { text: "Make me feel deeply", scores: { emotional: 5, heart: 3 } },
      { text: "Both equally", scores: { mind: 3, emotional: 3 } },
      { text: "Just entertain me", scores: { plot: 3, fast: 2 } }
    ]
  },
  {
    id: 9,
    question: "What are you seeking?",
    subtitle: "The soul of your search",
    weight: 2,
    answers: [
      { text: "Escape and adventure", scores: { scifi: 2, fantasy: 2, plot: 3 } },
      { text: "Understanding and insight", scores: { nonfiction: 3, mind: 3, literary: 2 } },
      { text: "Emotional connection", scores: { character: 3, heart: 3, emotional: 3 } },
      { text: "Suspense and mystery", scores: { thriller: 3, mystery: 3, plot: 2 } }
    ]
  },
  {
    id: 10,
    question: "Any content to avoid?",
    subtitle: "Deal-breakers that would ruin the experience",
    weight: 2,
    answers: [
      { text: "No graphic violence", scores: { dark: -5, thriller: -3 } },
      { text: "No sad/tragic endings", scores: { optimistic: 5, dark: -5 } },
      { text: "No explicit sexual content", scores: { dark: -3 } },
      { text: "No romance subplots", scores: { heart: -3 } },
      { text: "I'm open to anything", scores: {} }
    ]
  }
];

export default function BookRecommendationApp() {

  // --- GOOGLE ANALYTICS SHOUT ---
  useEffect(() => {
    ReactGA.send({ 
      hitType: "pageview", 
      page: window.location.pathname 
    });
  }, []); 
  // ------------------------------
  const [stage, setStage] = useState('intro');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [scores, setScores] = useState({});
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [selectedPageRange, setSelectedPageRange] = useState(null);
  const [selectedYearRange, setSelectedYearRange] = useState(null);
  const [recommendedBook, setRecommendedBook] = useState(null);
  const [similarBooks, setSimilarBooks] = useState([]);
  const [expandedBook, setExpandedBook] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [answerHistory, setAnswerHistory] = useState([]);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [showWishlist, setShowWishlist] = useState(false);
  const [readList, setReadList] = useState([]);
  const [showReadList, setShowReadList] = useState(false);
  const [showReadConfirmModal, setShowReadConfirmModal] = useState(false);
  const [pendingReadBook, setPendingReadBook] = useState(null);
  const [similarBooksFilter, setSimilarBooksFilter] = useState('default');
  const [browseSortOption, setBrowseSortOption] = useState('default');
  const [searchQuery, setSearchQuery] = useState('');
  const cardRefs = React.useRef({});
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = React.useRef(0);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('vibeBookDarkMode');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Hide header buttons on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Only hide after scrolling down at least 100px
      if (currentScrollY > 100) {
        if (currentScrollY > lastScrollY.current) {
          // Scrolling down
          setHeaderVisible(false);
          setShowMobileMenu(false); // Close menu when scrolling
        } else {
          // Scrolling up
          setHeaderVisible(true);
        }
      } else {
        // Near top of page, always show
        setHeaderVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load wishlist from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('vibeBookWishlist');
    if (saved) {
      setWishlist(JSON.parse(saved));
    }
  }, []);

  // Save wishlist to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('vibeBookWishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  // Load readList from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('vibeBookReadList');
    if (saved) {
      setReadList(JSON.parse(saved));
    }
  }, []);

  // Save readList to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('vibeBookReadList', JSON.stringify(readList));
  }, [readList]);

  // Save darkMode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('vibeBookDarkMode', JSON.stringify(darkMode));
  }, [darkMode]);

const handleAnswer = (answerScores, questionWeight = 1, answerText = null, pageRange = null, yearRange = null) => {
    setIsAnimating(true);

    // Scroll to top when answering
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Capture genre selection on Q1
    if (currentQuestion === 0 && answerText && GENRE_MAP[answerText]) {
      setSelectedGenre(answerText);
    }

    // Capture page range selection on Q2
    if (currentQuestion === 1) {
      setSelectedPageRange(pageRange);
    }

    // Capture year range selection on Q3
    if (currentQuestion === 2) {
      setSelectedYearRange(yearRange);
    }

    setTimeout(() => {
      const newScores = { ...scores };
      Object.entries(answerScores).forEach(([key, value]) => {
        const weightedValue = value * questionWeight;
        newScores[key] = (newScores[key] || 0) + weightedValue;
      });

      setAnswerHistory([...answerHistory, { questionId: currentQuestion, scores: answerScores, weight: questionWeight }]);
      setScores(newScores);

      if (currentQuestion < QUESTIONS.length - 1) {
        // Track each question answered
        ReactGA.event({
          category: "Quiz",
          action: "Answered Question",
          label: `Question ${currentQuestion + 1}: ${QUESTIONS[currentQuestion].question}`,
          value: currentQuestion + 1
        });
        setCurrentQuestion(currentQuestion + 1);
        setIsAnimating(false);
      } else {
        const recommendation = calculateRecommendation(newScores);
        setRecommendedBook(recommendation);

        const similar = findSimilarBooks(recommendation, newScores, 6);
        setSimilarBooks(similar);

        // Track quiz completion with recommended book
        ReactGA.event("quiz_completed", {
          book_title: recommendation.title,
          book_author: recommendation.author,
          book_isbn: recommendation.isbn
        });

        setStage('result');
        setIsAnimating(false);
      }
    }, 300);
  };

  const goBack = () => {
    if (currentQuestion > 0) {
      setIsAnimating(true);

      setTimeout(() => {
        const newHistory = [...answerHistory];
        const lastAnswer = newHistory.pop();
        setAnswerHistory(newHistory);

        const newScores = { ...scores };
        if (lastAnswer) {
          Object.entries(lastAnswer.scores).forEach(([key, value]) => {
            const weightedValue = value * lastAnswer.weight;
            newScores[key] = (newScores[key] || 0) - weightedValue;
          });
        }
        setScores(newScores);

        // Clear genre if going back to Q1
        if (currentQuestion === 1) {
          setSelectedGenre(null);
        }

        // Clear page range if going back to Q2
        if (currentQuestion === 2) {
          setSelectedPageRange(null);
        }

        // Clear year range if going back to Q3
        if (currentQuestion === 3) {
          setSelectedYearRange(null);
        }

        setCurrentQuestion(currentQuestion - 1);
        setIsAnimating(false);
      }, 200);
    }
  };

const surpriseMe = () => {
    // Scroll to top when getting new recommendation
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Filter out books the user has already read
    const availableBooks = BOOKS.filter(book => !readList.includes(book.id));

    if (availableBooks.length === 0) {
      // User has read all books! Fall back to all books
      const randomBook = BOOKS[Math.floor(Math.random() * BOOKS.length)];
      setRecommendedBook(randomBook);
      setSimilarBooks([]);
      setStage('result');
      return;
    }

    const randomBook = availableBooks[Math.floor(Math.random() * availableBooks.length)];

    const shownBooks = JSON.parse(localStorage.getItem('vibeBookShown') || '[]');
    shownBooks.push(randomBook.id);
    localStorage.setItem('vibeBookShown', JSON.stringify(shownBooks));

    setRecommendedBook(randomBook);

    const similar = availableBooks
      .filter(b => b.id !== randomBook.id)
      .sort(() => 0.5 - Math.random())
      .slice(0, 6);
    setSimilarBooks(similar);

    setStage('result');
  };

  const getReadingTime = (pageCount) => {
    const minutesPerPage = 2;
    const totalMinutes = pageCount * minutesPerPage;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours === 0) return `${minutes} min read`;
    if (minutes === 0) return `${hours} hr read`;
    return `${hours}h ${minutes}m read`;
  };

  const getScoreLabel = (key) => {
    const labels = {
      scifi: 'science fiction',
      fantasy: 'fantasy worlds',
      contemporary: 'contemporary settings',
      literary: 'literary depth',
      thriller: 'thrilling suspense',
      mystery: 'mystery elements',
      character: 'character-driven stories',
      plot: 'plot-driven narratives',
      fast: 'fast-paced action',
      slow: 'slow-burn storytelling',
      medium: 'balanced pacing',
      optimistic: 'uplifting themes',
      dark: 'darker themes',
      atmosphere: 'immersive atmosphere',
      standalone: 'standalone stories',
      series: 'series narratives',
      modern: 'modern writing',
      classic: 'classic style',
      mind: 'intellectual depth',
      heart: 'emotional resonance',
      emotional: 'emotional impact'
    };
    return labels[key] || key;
  };

  const generateExplanation = (userScores, recommendedBook) => {
    // Calculate which scores contributed most to the match
    const contributions = Object.entries(userScores)
      .map(([key, userValue]) => ({
        key,
        score: (recommendedBook.scores[key] || 0) * userValue
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);

    const reasons = contributions
      .filter(c => c.score > 0)
      .map(c => getScoreLabel(c.key));

    if (reasons.length === 0) return "This book matches your unique preferences.";
    if (reasons.length === 1) return `Based on your preference for ${reasons[0]}.`;
    if (reasons.length === 2) return `Based on your preferences for ${reasons[0]} and ${reasons[1]}.`;
    return `Based on your preferences for ${reasons.slice(0, -1).join(', ')}, and ${reasons[reasons.length - 1]}.`;
  };

  const calculateRecommendation = (userScores, excludeReadList = null) => {
    let bestMatch = null;
    let highestScore = -1;

    // Use provided exclusion list or fall back to current readList state
    const booksToExclude = excludeReadList || readList;

    // Filter books by selected genre first
    const genreCategories = selectedGenre ? GENRE_MAP[selectedGenre] : null;
    let filteredBooks = genreCategories
      ? BOOKS.filter(book =>
          book.categories.some(cat => genreCategories.includes(cat))
        )
      : BOOKS;

    // Filter out books the user has already read
    filteredBooks = filteredBooks.filter(book => !booksToExclude.includes(book.id));

    // Filter by page count if selected
    if (selectedPageRange) {
      filteredBooks = filteredBooks.filter(book =>
        book.pageCount >= selectedPageRange.min && book.pageCount <= selectedPageRange.max
      );
    }

    // Filter by publication year if selected
    if (selectedYearRange) {
      filteredBooks = filteredBooks.filter(book =>
        book.publicationYear >= selectedYearRange.min && book.publicationYear <= selectedYearRange.max
      );
    }

    // If no books match the filters, fall back to genre-only (still excluding read books), then all books
    let booksToSearch = filteredBooks;
    if (booksToSearch.length === 0 && genreCategories) {
      booksToSearch = BOOKS.filter(book =>
        book.categories.some(cat => genreCategories.includes(cat)) &&
        !booksToExclude.includes(book.id)
      );
    }
    if (booksToSearch.length === 0) {
      booksToSearch = BOOKS.filter(book => !booksToExclude.includes(book.id));
    }

    booksToSearch.forEach(book => {
      let totalScore = 0;
      Object.entries(userScores).forEach(([key, userValue]) => {
        const bookValue = book.scores[key] || 0;
        totalScore += bookValue * userValue;
      });

      if (totalScore > highestScore) {
        highestScore = totalScore;
        bestMatch = book;
      }
    });

    return bestMatch;
  };

  const findSimilarBooks = (mainBook, userScores, count, excludeReadList = null) => {
    // Use provided exclusion list or fall back to current readList state
    const booksToExclude = excludeReadList || readList;

    // Filter by selected genre first
    const genreCategories = selectedGenre ? GENRE_MAP[selectedGenre] : null;
    let filteredBooks = genreCategories
      ? BOOKS.filter(book =>
          book.categories.some(cat => genreCategories.includes(cat))
        )
      : BOOKS;

    // Filter out books the user has already read
    filteredBooks = filteredBooks.filter(book => !booksToExclude.includes(book.id));

    // Filter by page count if selected
    if (selectedPageRange) {
      filteredBooks = filteredBooks.filter(book =>
        book.pageCount >= selectedPageRange.min && book.pageCount <= selectedPageRange.max
      );
    }

    // Filter by publication year if selected
    if (selectedYearRange) {
      filteredBooks = filteredBooks.filter(book =>
        book.publicationYear >= selectedYearRange.min && book.publicationYear <= selectedYearRange.max
      );
    }

    const scored = filteredBooks
      .filter(book => book.id !== mainBook.id)
      .map(book => {
        let similarityScore = 0;

        // Score based on shared categories
        mainBook.categories.forEach(cat => {
          if (book.categories.includes(cat)) similarityScore += 10;
        });

        // Score based on similar attributes
        Object.entries(mainBook.scores).forEach(([key, value]) => {
          const bookValue = book.scores[key] || 0;
          const diff = Math.abs(value - bookValue);
          similarityScore += (10 - diff);
        });

        // Bonus for user preferences
        Object.entries(userScores).forEach(([key, userValue]) => {
          const bookValue = book.scores[key] || 0;
          similarityScore += bookValue * (userValue * 0.5);
        });

        return { book, score: similarityScore };
      })
      .sort((a, b) => b.score - a.score);

    // FIXED: Ensure no duplicates by checking IDs
    const uniqueBooks = [];
    const seenIds = new Set([mainBook.id]); // Start with main book ID

    for (const item of scored) {
      if (!seenIds.has(item.book.id) && uniqueBooks.length < count) {
        uniqueBooks.push(item.book);
        seenIds.add(item.book.id);
      }
    }

    return uniqueBooks;
  };

  const reset = () => {
    // This is the new line for Google!
    ReactGA.event({ category: "User", action: "Clicked Start Over" });
    setStage('intro');
    setCurrentQuestion(0);
    setScores({});
    setSelectedGenre(null);
    setSelectedPageRange(null);
    setSelectedYearRange(null);
    setRecommendedBook(null);
    setSimilarBooks([]);
    setExpandedBook(null);
    setIsAnimating(false);
  };

  const handleShare = async () => {
    if (!recommendedBook) return;

    const shareText = `I just found my perfect book match on VibeShelf: "${recommendedBook.title}" by ${recommendedBook.author}! 📚\n\nFind your book vibe at: ${window.location.origin}`;

    // Try Web Share API first (works on mobile and some modern browsers)
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My VibeShelf Book Recommendation',
          text: shareText,
        });
        ReactGA.event({ category: "User", action: "Shared Recommendation (Native)" });
      } catch (err) {
        // User cancelled or error occurred
        if (err.name !== 'AbortError') {
          console.log('Share failed:', err);
        }
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareText);
        setShareSuccess(true);
        ReactGA.event({ category: "User", action: "Shared Recommendation (Clipboard)" });
        setTimeout(() => setShareSuccess(false), 3000);
      } catch (err) {
        console.log('Copy to clipboard failed:', err);
      }
    }
  };

  const toggleWishlist = (bookId) => {
    if (wishlist.includes(bookId)) {
      setWishlist(wishlist.filter(id => id !== bookId));
      ReactGA.event({ category: "User", action: "Removed from Wishlist" });
    } else {
      setWishlist([...wishlist, bookId]);
      ReactGA.event({ category: "User", action: "Added to Wishlist" });
    }
  };

  const getWishlistBooks = () => {
    return BOOKS.filter(book => wishlist.includes(book.id));
  };

  const toggleReadList = (bookId) => {
    if (readList.includes(bookId)) {
      setReadList(readList.filter(id => id !== bookId));
      ReactGA.event("read_list_remove", { book_id: bookId });
    } else {
      setReadList([...readList, bookId]);
      ReactGA.event("read_list_add", { book_id: bookId });
    }
  };

  const getReadListBooks = () => {
    return BOOKS.filter(book => readList.includes(book.id));
  };

  const handleMarkAsRead = (book) => {
    // Add to read list
    if (!readList.includes(book.id)) {
      setReadList([...readList, book.id]);
      ReactGA.event("marked_as_read", { book_title: book.title, book_author: book.author });
    }

    // Close modal
    setShowReadConfirmModal(false);
    setPendingReadBook(null);

    // Recalculate recommendation if this was the main recommended book
    if (recommendedBook && book.id === recommendedBook.id) {
      const newRecommendation = calculateRecommendation(scores, [...readList, book.id]);
      setRecommendedBook(newRecommendation);

      if (newRecommendation) {
        const similar = findSimilarBooks(newRecommendation, scores, 6, [...readList, book.id]);
        setSimilarBooks(similar);
      }

      // Scroll to top to show new recommendation
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Just update similar books to exclude this one
      const updatedSimilar = similarBooks.filter(b => b.id !== book.id);
      // If we removed one, try to get a replacement
      if (updatedSimilar.length < 6 && recommendedBook) {
        const newSimilar = findSimilarBooks(recommendedBook, scores, 6, [...readList, book.id]);
        setSimilarBooks(newSimilar);
      } else {
        setSimilarBooks(updatedSimilar);
      }
    }
  };

  const getFilteredSimilarBooks = () => {
    let filtered = [...similarBooks];

    switch (similarBooksFilter) {
      case 'rating':
        return filtered.sort((a, b) => b.rating - a.rating);
      case 'year-new':
        return filtered.sort((a, b) => (b.publicationYear || 0) - (a.publicationYear || 0));
      case 'year-old':
        return filtered.sort((a, b) => (a.publicationYear || 0) - (b.publicationYear || 0));
      case 'pages-short':
        return filtered.sort((a, b) => a.pageCount - b.pageCount);
      case 'pages-long':
        return filtered.sort((a, b) => b.pageCount - a.pageCount);
      default:
        return filtered; // Original recommendation order
    }
  };

  const getSearchResults = () => {
    if (!searchQuery.trim()) return BOOKS;

    const query = searchQuery.toLowerCase().trim();

    // Map common abbreviations to full genre names
    const genreAliases = {
      'scifi': 'science fiction',
      'sci-fi': 'science fiction',
      'sci fi': 'science fiction',
      'sf': 'science fiction',
      'fantasy': 'fantasy',
      'contemporary': 'contemporary',
      'literary': 'literary fiction',
      'thriller': 'thriller',
      'mystery': 'mystery',
      'romance': 'romance',
      'horror': 'horror',
      'historical': 'historical',
      'dystopian': 'dystopian'
    };

    // Check if query matches any alias and expand it
    const expandedQuery = genreAliases[query] || query;

    return BOOKS.filter(book =>
      book.title.toLowerCase().includes(query) ||
      book.author.toLowerCase().includes(query) ||
      book.categories.some(cat =>
        cat.toLowerCase().includes(query) ||
        cat.toLowerCase().includes(expandedQuery)
      ) ||
      book.synopsis.toLowerCase().includes(query) ||
      book.synopsis.toLowerCase().includes(expandedQuery)
    );
  };

  const getSortedSearchResults = () => {
    let results = [...getSearchResults()];

    switch (browseSortOption) {
      case 'rating':
        return results.sort((a, b) => b.rating - a.rating);
      case 'year-new':
        return results.sort((a, b) => (b.publicationYear || 0) - (a.publicationYear || 0));
      case 'year-old':
        return results.sort((a, b) => (a.publicationYear || 0) - (b.publicationYear || 0));
      case 'title-az':
        return results.sort((a, b) => a.title.localeCompare(b.title));
      case 'title-za':
        return results.sort((a, b) => b.title.localeCompare(a.title));
      case 'pages-short':
        return results.sort((a, b) => a.pageCount - b.pageCount);
      case 'pages-long':
        return results.sort((a, b) => b.pageCount - a.pageCount);
      default:
        // Default: alphabetical by title if no search, relevance if searching
        return searchQuery.trim() ? results : results.sort((a, b) => a.title.localeCompare(b.title));
    }
  };

  const getAmazonLink = (isbn) => {
    return `https://www.amazon.com/dp/${isbn}?tag=vibebookrec-20&linkCode=ogi&th=1&psc=1`;
  };

  const BookCard = ({ book, isExpanded, onToggle, isFeatured = false, cardRef }) => {
    const [imageError, setImageError] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    
    // Multiple image sources - trying CORS-friendly options with dimension validation
    const getImageSources = () => {
      const sources = [
        // Try Open Library first (most CORS-friendly)
        `https://covers.openlibrary.org/b/isbn/${book.isbn}-L.jpg`,
        // Amazon images (usually work)
        `https://images-na.ssl-images-amazon.com/images/P/${book.isbn}.01.LZZZZZZZ.jpg`,
        `https://m.media-amazon.com/images/P/${book.isbn}.01._SCLZZZZZZZ_SX500_.jpg`,
        // Google Books from our database
        book.image,
      ];
      return sources.filter(Boolean);
    };

// eslint-disable-next-line react-hooks/exhaustive-deps
const imageSources = React.useMemo(() => getImageSources(), [book.isbn]);

    useEffect(() => {
      console.log('📚 Trying to load image for:', book.title);
      console.log('   Source', currentImageIndex + 1, 'of', imageSources.length + ':', imageSources[currentImageIndex]);
    }, [currentImageIndex, book.title, imageSources]);

    const handleImageError = () => {
      console.log('❌ Image source', currentImageIndex + 1, 'failed for:', book.title);
      if (currentImageIndex < imageSources.length - 1) {
        console.log('   Trying next source...');
        setCurrentImageIndex(currentImageIndex + 1);
      } else {
        console.log('   All sources failed, showing placeholder');
        setImageError(true);
      }
    };
    
    const handleImageLoad = (e) => {
      // Check if the loaded image is a tiny placeholder (like Amazon's 1x1 GIF)
      const img = e.target;
      if (img.naturalWidth < 50 || img.naturalHeight < 50) {
        console.log('⚠️ Image too small (placeholder detected):', img.naturalWidth, 'x', img.naturalHeight, 'for:', book.title);
        handleImageError();
        return;
      }
      console.log('✅ Image loaded successfully from source', currentImageIndex + 1, 'for:', book.title, '(' + img.naturalWidth + 'x' + img.naturalHeight + ')');
    };
    
    return (
      <div
        ref={cardRef}
        className={`${isExpanded ? 'h-auto' : 'h-full'} flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg overflow-hidden transition-all duration-300 ${
        isExpanded ? 'col-span-full' : ''
      } ${isFeatured ? '' : 'hover:shadow-xl'} relative`}>

        {/* Already Read Button - Top Left */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (readList.includes(book.id)) {
              toggleReadList(book.id);
            } else {
              setPendingReadBook(book);
              setShowReadConfirmModal(true);
            }
          }}
          className={`absolute top-4 left-4 z-10 ${darkMode ? 'bg-gray-700' : 'bg-white'} rounded-full p-2 shadow-md hover:shadow-lg transition-all duration-300 group`}
          title={readList.includes(book.id) ? "Remove from Read List" : "Mark as Read"}
        >
          {readList.includes(book.id) ? (
            <CheckCircle className="w-5 h-5 text-green-600 fill-green-600" />
          ) : (
            <BookCheck className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-400'} group-hover:text-green-600 transition-colors`} />
          )}
        </button>

        {/* Wishlist Button - Top Right */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleWishlist(book.id);
          }}
          className={`absolute top-4 right-4 z-10 ${darkMode ? 'bg-gray-700' : 'bg-white'} rounded-full p-2 shadow-md hover:shadow-lg transition-all duration-300 group`}
          title={wishlist.includes(book.id) ? "Remove from Reading List" : "Add to Reading List"}
        >
          {wishlist.includes(book.id) ? (
            <BookmarkCheck className="w-5 h-5 text-orange-600 fill-orange-600" />
          ) : (
            <Bookmark className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-400'} group-hover:text-orange-600 transition-colors`} />
          )}
        </button>

        <div className={`flex flex-col flex-1 ${isExpanded || isFeatured ? 'md:flex-row' : ''}`}>
          <div className={`${
            isExpanded || isFeatured ? 'md:w-2/5' : 'w-full'
          } ${darkMode ? 'bg-gradient-to-br from-gray-700 to-gray-600' : 'bg-gradient-to-br from-orange-100 to-red-100'} ${isExpanded ? 'p-4' : 'p-6'} flex items-center justify-center min-h-[400px]`}>
            {!imageError ? (
              <img
                key={currentImageIndex}
                src={imageSources[currentImageIndex]}
                alt={book.title}
                onError={handleImageError}
                onLoad={handleImageLoad}
                className="max-w-full h-auto rounded-lg shadow-lg"
                style={{ maxHeight: isFeatured || isExpanded ? '400px' : '250px' }}
              />
            ) : (
              <div 
                className="w-full flex items-center justify-center rounded-lg bg-gradient-to-br from-orange-200 to-red-200 shadow-lg"
                style={{ height: isExpanded || isFeatured ? '400px' : '250px', maxWidth: '300px' }}
              >
                <div className="text-center px-6">
                  <img src="/logo.svg" alt="VibeShelf" className="w-16 h-16 mx-auto mb-4" />
                  <p className="text-gray-800 font-serif font-bold text-lg leading-tight">
                    {book.title}
                  </p>
                  <p className="text-gray-600 font-serif italic text-sm mt-2">
                    by {book.author}
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <div className={`${isExpanded || isFeatured ? 'md:w-3/5' : 'w-full'} ${isExpanded ? 'p-4' : 'p-6'} flex flex-col ${isFeatured ? 'min-h-[480px]' : isExpanded ? 'min-h-[540px]' : 'flex-1'}`}>
            <h3 className={`${isFeatured ? 'text-3xl' : 'text-2xl'} font-bold ${darkMode ? 'text-white' : 'text-gray-900'} font-serif mb-2 line-clamp-2`} style={{ height: isFeatured ? 'auto' : isExpanded ? 'auto' : '4rem' }}>
              {book.title}
            </h3>
            <p className={`${isFeatured ? 'text-xl' : 'text-lg'} ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4 font-serif italic`} style={{ height: isFeatured ? 'auto' : isExpanded ? 'auto' : '2rem' }}>
              by {book.author}{book.publicationYear ? ` (${book.publicationYear})` : ''}
            </p>

            <div className="flex items-center gap-2 mb-4">
              <StarRating rating={book.rating} size="w-5 h-5" color="yellow" />
              <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} font-sans`}>{book.rating}</span>
              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} font-sans`}>
                ({book.ratingsCount.toLocaleString()} reviews)
              </span>
            </div>

            <div className={`flex flex-wrap gap-3 mb-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <div className="flex items-center gap-1">
                <BookOpen className="w-4 h-4" />
                <span>{book.pageCount} pages</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{getReadingTime(book.pageCount)}</span>
              </div>
            </div>
            
            <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed font-sans mb-6 flex-grow ${
              !isExpanded && !isFeatured ? 'line-clamp-5 overflow-hidden' : ''
            }`}>
              {book.synopsis}
            </p>

            {(isExpanded || isFeatured) && (
              <div className="flex flex-wrap gap-2 mb-4">
                {book.categories.map((cat, idx) => (
                  <span key={idx} className={`px-3 py-1 ${darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'} rounded-full text-sm font-sans`}>
                    {cat}
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-col gap-3 mt-auto">
              {!isFeatured && !isExpanded && (
                <button
                  onClick={onToggle}
                  className={`flex-1 ${darkMode ? 'bg-gray-700 text-orange-400 border-orange-400 hover:bg-gray-600' : 'bg-white text-orange-600 border-orange-600 hover:bg-orange-50'} border-2 px-6 py-3 rounded-full font-sans font-semibold transition-all duration-300 inline-flex items-center justify-center gap-2`}
                >
                  View More Details
                  <ChevronDown className="w-4 h-4" />
                </button>
              )}
              
              <a
                href={getAmazonLink(book.isbn)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => ReactGA.event("amazon_click", { book_title: book.title, book_author: book.author, book_isbn: book.isbn })}
                className="flex-1 bg-orange-600 text-white px-6 py-3 rounded-full font-sans font-semibold hover:bg-orange-700 transition-all duration-300 inline-flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
              >
                Get This Book
                <ExternalLink className="w-4 h-4" />
              </a>

              {/* Already Read Button - Only on Featured/Main Recommendation */}
              {isFeatured && (
                readList.includes(book.id) ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleReadList(book.id);
                    }}
                    className={`flex-1 ${darkMode ? 'bg-green-900/50 text-green-400 border-green-600 hover:bg-green-900/70' : 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200'} border-2 px-6 py-3 rounded-full font-sans font-semibold transition-all duration-300 inline-flex items-center justify-center gap-2`}
                  >
                    <CheckCircle className="w-4 h-4 fill-green-600" />
                    Already Read
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingReadBook(book);
                      setShowReadConfirmModal(true);
                    }}
                    className={`flex-1 ${darkMode ? 'bg-gray-700 text-green-400 border-green-500 hover:bg-gray-600' : 'bg-white text-green-600 border-green-500 hover:bg-green-50'} border-2 px-6 py-3 rounded-full font-sans font-semibold transition-all duration-300 inline-flex items-center justify-center gap-2`}
                  >
                    <Check className="w-4 h-4" />
                    I've Read This
                  </button>
                )
              )}

              {!isFeatured && isExpanded && (
                <button
                  onClick={onToggle}
                  className={`flex-1 ${darkMode ? 'bg-gray-700 text-orange-400 border-orange-400 hover:bg-gray-600' : 'bg-white text-orange-600 border-orange-600 hover:bg-orange-50'} border-2 px-6 py-3 rounded-full font-sans font-semibold transition-all duration-300 inline-flex items-center justify-center gap-2`}
                >
                  Show Less
                  <ChevronUp className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };




  const WishlistModal = () => {
    const wishlistBooks = getWishlistBooks();

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowWishlist(false)}>
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 font-serif">My Reading List</h2>
              <p className="text-gray-600 font-sans mt-1">{wishlistBooks.length} book{wishlistBooks.length !== 1 ? 's' : ''} saved</p>
            </div>
            <button
              onClick={() => setShowWishlist(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6">
            {wishlistBooks.length === 0 ? (
              <div className="text-center py-12">
                <Bookmark className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-xl text-gray-500 font-sans">Your reading list is empty</p>
                <p className="text-gray-400 font-sans mt-2">Click the bookmark icon on any book to save it here</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {wishlistBooks.map(book => (
                  <div key={book.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all duration-300">
                    <div className="flex gap-4">
                      <div className="w-20 h-28 bg-gradient-to-br from-orange-100 to-red-100 rounded flex-shrink-0 flex items-center justify-center">
                        <img src="/logo.svg" alt="VibeShelf" className="w-8 h-8" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 font-serif text-lg line-clamp-2">{book.title}</h3>
                        <p className="text-gray-600 font-serif italic text-sm mt-1">by {book.author}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <StarRating rating={book.rating} size="w-4 h-4" color="orange" />
                          <span className="text-xs text-gray-500">{book.rating}</span>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <a
                            href={getAmazonLink(book.isbn)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => ReactGA.event("amazon_click", { book_title: book.title, book_author: book.author, book_isbn: book.isbn })}
                            className="text-sm bg-orange-600 text-white px-4 py-2 rounded-full font-sans font-semibold hover:bg-orange-700 transition-all inline-flex items-center gap-1"
                          >
                            Buy Now
                            <ExternalLink className="w-3 h-3" />
                          </a>
                          <button
                            onClick={() => toggleWishlist(book.id)}
                            className="text-sm bg-white text-gray-600 border border-gray-300 px-4 py-2 rounded-full font-sans hover:bg-gray-50 transition-all"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Confirmation Modal for marking book as read
  const ReadConfirmModal = () => {
    if (!pendingReadBook) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => { setShowReadConfirmModal(false); setPendingReadBook(null); }}>
        <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="p-6">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 font-serif text-center mb-2">
              Already read this one?
            </h2>
            <p className="text-gray-600 font-sans text-center mb-2">
              <span className="font-semibold">{pendingReadBook.title}</span>
            </p>
            <p className="text-gray-500 font-sans text-center text-sm mb-6">
              We'll add it to your Read List and find you something new!
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowReadConfirmModal(false); setPendingReadBook(null); }}
                className="flex-1 bg-white text-gray-600 border border-gray-300 px-6 py-3 rounded-full font-sans font-semibold hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => handleMarkAsRead(pendingReadBook)}
                className="flex-1 bg-green-600 text-white px-6 py-3 rounded-full font-sans font-semibold hover:bg-green-700 transition-all inline-flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                Yes, I've Read It
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Read List Modal (books already read)
  const ReadListModal = () => {
    const readListBooks = getReadListBooks();

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowReadList(false)}>
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 font-serif">Books I've Read</h2>
              <p className="text-gray-600 font-sans mt-1">{readListBooks.length} book{readListBooks.length !== 1 ? 's' : ''} completed</p>
            </div>
            <button
              onClick={() => setShowReadList(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6">
            {readListBooks.length === 0 ? (
              <div className="text-center py-12">
                <BookCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-xl text-gray-500 font-sans">No books marked as read yet</p>
                <p className="text-gray-400 font-sans mt-2">When you've already read a recommendation, mark it as read to get new suggestions</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {readListBooks.map(book => (
                  <div key={book.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all duration-300 bg-green-50">
                    <div className="flex gap-4">
                      <div className="w-20 h-28 bg-gradient-to-br from-green-100 to-emerald-100 rounded flex-shrink-0 flex items-center justify-center">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 font-serif text-lg line-clamp-2">{book.title}</h3>
                        <p className="text-gray-600 font-serif italic text-sm mt-1">by {book.author}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <StarRating rating={book.rating} size="w-4 h-4" color="orange" />
                          <span className="text-xs text-gray-500">{book.rating}</span>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => toggleReadList(book.id)}
                            className="text-sm bg-white text-gray-600 border border-gray-300 px-4 py-2 rounded-full font-sans hover:bg-gray-50 transition-all"
                          >
                            Remove from Read
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Mobile Menu Dropdown
  const MobileMenuDropdown = () => {
    if (!showMobileMenu) return null;

    return (
      <div
        className="fixed inset-0 z-50 sm:hidden"
        onClick={() => setShowMobileMenu(false)}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black bg-opacity-25" />

        {/* Menu Panel */}
        <div
          className="absolute top-16 right-4 bg-white rounded-2xl shadow-2xl overflow-hidden w-64 animate-scale-in"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-2">
            {/* Reading List Option */}
            <button
              onClick={() => {
                setShowMobileMenu(false);
                setShowWishlist(true);
              }}
              className="w-full flex items-center gap-4 p-4 hover:bg-orange-50 rounded-xl transition-colors"
            >
              <div className="relative">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Bookmark className="w-5 h-5 text-orange-600" />
                </div>
                {wishlist.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-sans font-bold">
                    {wishlist.length}
                  </span>
                )}
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-900 font-sans">My Reading List</p>
                <p className="text-sm text-gray-500 font-sans">{wishlist.length} book{wishlist.length !== 1 ? 's' : ''} saved</p>
              </div>
            </button>

            {/* Divider */}
            <div className="h-px bg-gray-200 mx-4" />

            {/* Books I've Read Option */}
            <button
              onClick={() => {
                setShowMobileMenu(false);
                setShowReadList(true);
              }}
              className="w-full flex items-center gap-4 p-4 hover:bg-green-50 rounded-xl transition-colors"
            >
              <div className="relative">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <BookCheck className="w-5 h-5 text-green-600" />
                </div>
                {readList.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-sans font-bold">
                    {readList.length}
                  </span>
                )}
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-900 font-sans">Books I've Read</p>
                <p className="text-sm text-gray-500 font-sans">{readList.length} book{readList.length !== 1 ? 's' : ''} completed</p>
              </div>
            </button>

            {/* Divider */}
            <div className="h-px bg-gray-200 mx-4" />

            {/* Dark Mode Toggle */}
            <button
              onClick={() => {
                setDarkMode(!darkMode);
                setShowMobileMenu(false);
              }}
              className="w-full flex items-center gap-4 p-4 hover:bg-purple-50 rounded-xl transition-colors"
            >
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                {darkMode ? <Sun className="w-5 h-5 text-purple-600" /> : <Moon className="w-5 h-5 text-purple-600" />}
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-900 font-sans">{darkMode ? 'Light Mode' : 'Dark Mode'}</p>
                <p className="text-sm text-gray-500 font-sans">Switch appearance</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Browse Screen
  if (stage === 'browse') {
    const searchResults = getSortedSearchResults();

    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800' : 'bg-gradient-to-br from-amber-50 via-orange-50 to-red-50'} py-12 px-4`}>
        {showWishlist && <WishlistModal />}
        {showReadList && <ReadListModal />}
        {showReadConfirmModal && <ReadConfirmModal />}
        <MobileMenuDropdown />

        {/* Home Button */}
        <button
          onClick={() => {
            setStage('intro');
            setSearchQuery('');
            setExpandedBook(null);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className={`fixed top-6 left-6 bg-white text-orange-600 border-2 border-orange-600 px-3 sm:px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-40 inline-flex items-center gap-2 font-sans font-semibold ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          title="Back to Home"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="hidden sm:inline">Home</span>
        </button>

        {/* Mobile Menu Button - visible only on mobile */}
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          className={`fixed top-6 right-6 sm:hidden bg-white text-gray-700 border-2 border-gray-300 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-40 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          title="Menu"
        >
          {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          {(wishlist.length > 0 || readList.length > 0) && !showMobileMenu && (
            <span className="absolute -top-1 -right-1 bg-orange-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-sans font-bold">
              {wishlist.length + readList.length}
            </span>
          )}
        </button>

        {/* Wishlist Button - hidden on mobile, visible on sm+ */}
        <button
          onClick={() => setShowWishlist(true)}
          className={`fixed top-6 right-6 hidden sm:block bg-white text-orange-600 border-2 border-orange-600 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-40 group ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          title="View Reading List"
        >
          <div className="relative">
            <Bookmark className="w-6 h-6" />
            {wishlist.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-orange-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-sans font-bold">
                {wishlist.length}
              </span>
            )}
          </div>
        </button>

        {/* Read List Button - hidden on mobile, visible on sm+ */}
        <button
          onClick={() => setShowReadList(true)}
          className={`fixed top-6 right-20 hidden sm:block bg-white text-green-600 border-2 border-green-500 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-40 group ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          title="View Books I've Read"
        >
          <div className="relative">
            <BookCheck className="w-6 h-6" />
            {readList.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-sans font-bold">
                {readList.length}
              </span>
            )}
          </div>
        </button>

        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold text-gray-900 font-serif mb-4">Browse All Books</h1>
            <p className="text-lg text-gray-600 font-sans">{BOOKS.length} curated books to discover</p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by title, author, genre, or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 pr-12 rounded-full border-2 border-gray-300 focus:border-orange-600 focus:outline-none font-sans text-lg"
              />
              <svg className="absolute right-5 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {searchQuery && (
              <p className="text-center text-gray-600 font-sans mt-3">
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
              </p>
            )}
          </div>

          {/* Sort Options */}
          <div className="mb-8">
            <p className="text-center text-gray-600 font-sans text-sm mb-3">Sort by:</p>
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => setBrowseSortOption('default')}
                className={`px-4 py-2 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                  browseSortOption === 'default'
                    ? 'bg-orange-600 text-white shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-orange-500 hover:text-orange-400' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-600 hover:text-orange-600'
                }`}
              >
                Best Match
              </button>
              <button
                onClick={() => setBrowseSortOption('rating')}
                className={`px-4 py-2 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                  browseSortOption === 'rating'
                    ? 'bg-orange-600 text-white shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-orange-500 hover:text-orange-400' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-600 hover:text-orange-600'
                }`}
              >
                Highest Rated
              </button>
              <button
                onClick={() => setBrowseSortOption('year-new')}
                className={`px-4 py-2 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                  browseSortOption === 'year-new'
                    ? 'bg-orange-600 text-white shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-orange-500 hover:text-orange-400' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-600 hover:text-orange-600'
                }`}
              >
                Newest First
              </button>
              <button
                onClick={() => setBrowseSortOption('year-old')}
                className={`px-4 py-2 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                  browseSortOption === 'year-old'
                    ? 'bg-orange-600 text-white shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-orange-500 hover:text-orange-400' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-600 hover:text-orange-600'
                }`}
              >
                Oldest First
              </button>
              <button
                onClick={() => setBrowseSortOption('title-az')}
                className={`px-4 py-2 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                  browseSortOption === 'title-az'
                    ? 'bg-orange-600 text-white shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-orange-500 hover:text-orange-400' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-600 hover:text-orange-600'
                }`}
              >
                Title A-Z
              </button>
              <button
                onClick={() => setBrowseSortOption('title-za')}
                className={`px-4 py-2 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                  browseSortOption === 'title-za'
                    ? 'bg-orange-600 text-white shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-orange-500 hover:text-orange-400' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-600 hover:text-orange-600'
                }`}
              >
                Title Z-A
              </button>
              <button
                onClick={() => setBrowseSortOption('pages-short')}
                className={`px-4 py-2 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                  browseSortOption === 'pages-short'
                    ? 'bg-orange-600 text-white shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-orange-500 hover:text-orange-400' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-600 hover:text-orange-600'
                }`}
              >
                Shortest
              </button>
              <button
                onClick={() => setBrowseSortOption('pages-long')}
                className={`px-4 py-2 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                  browseSortOption === 'pages-long'
                    ? 'bg-orange-600 text-white shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-orange-500 hover:text-orange-400' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-600 hover:text-orange-600'
                }`}
              >
                Longest
              </button>
            </div>
          </div>

          {/* Books Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {searchResults.map(book => {
              if (!cardRefs.current[book.id]) {
                cardRefs.current[book.id] = React.createRef();
              }
              return (
                <BookCard
                  key={book.id}
                  book={book}
                  isExpanded={expandedBook === book.id}
                  cardRef={cardRefs.current[book.id]}
                  onToggle={() => {
                    const newExpandedState = expandedBook === book.id ? null : book.id;
                    setExpandedBook(newExpandedState);

                    if (newExpandedState === book.id) {
                      setTimeout(() => {
                        const cardElement = cardRefs.current[book.id]?.current;
                        if (cardElement) {
                          const rect = cardElement.getBoundingClientRect();
                          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                          const cardTop = rect.top + scrollTop;
                          const windowHeight = window.innerHeight;
                          const cardHeight = rect.height;
                          const targetScroll = cardTop - (windowHeight / 2) + (cardHeight / 2);

                          window.scrollTo({
                            top: Math.max(0, targetScroll),
                            behavior: 'smooth'
                          });
                        }
                      }, 100);
                    }
                  }}
                />
              );
            })}
          </div>

        </div>
      </div>
    );
  }

  // Intro Screen
  if (stage === 'intro') {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800' : 'bg-gradient-to-br from-amber-50 via-orange-50 to-red-50'} flex items-center justify-center p-4 font-serif`}>
        {showWishlist && <WishlistModal />}
        {showReadList && <ReadListModal />}
        {showReadConfirmModal && <ReadConfirmModal />}
        <MobileMenuDropdown />

        {/* Mobile Menu Button - visible only on mobile */}
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          className="fixed top-6 right-6 sm:hidden bg-white text-gray-700 border-2 border-gray-300 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-40"
          title="Menu"
        >
          {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          {(wishlist.length > 0 || readList.length > 0) && !showMobileMenu && (
            <span className="absolute -top-1 -right-1 bg-orange-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-sans font-bold">
              {wishlist.length + readList.length}
            </span>
          )}
        </button>

        {/* Wishlist Button - hidden on mobile, visible on sm+ */}
        <button
          onClick={() => setShowWishlist(true)}
          className="fixed top-6 right-6 hidden sm:block bg-white text-orange-600 border-2 border-orange-600 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-40 group"
          title="View Reading List"
        >
          <div className="relative">
            <Bookmark className="w-6 h-6" />
            {wishlist.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-orange-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-sans font-bold">
                {wishlist.length}
              </span>
            )}
          </div>
        </button>

        {/* Read List Button - hidden on mobile, visible on sm+ */}
        <button
          onClick={() => setShowReadList(true)}
          className="fixed top-6 right-20 hidden sm:block bg-white text-green-600 border-2 border-green-500 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-40 group"
          title="View Books I've Read"
        >
          <div className="relative">
            <BookCheck className="w-6 h-6" />
            {readList.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-sans font-bold">
                {readList.length}
              </span>
            )}
          </div>
        </button>

        {/* Dark Mode Toggle - hidden on mobile, visible on sm+ */}
        <button
          onClick={() => setDarkMode(!darkMode)}
          className={`fixed top-6 left-6 hidden sm:block ${darkMode ? 'bg-gray-800 text-yellow-400 border-yellow-400' : 'bg-white text-purple-600 border-purple-400'} border-2 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-40`}
          title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {darkMode ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
        </button>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=Karla:wght@400;500;600&display=swap');
          
          body {
            font-family: 'Karla', sans-serif;
          }
          
          .font-serif {
            font-family: 'Libre+Baskerville', serif;
          }
          
          .animate-float {
            animation: float 3s ease-in-out infinite;
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          
          .animate-fade-in {
            animation: fadeIn 0.6s ease-out;
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          .animate-fade-in-up {
            animation: fadeInUp 0.8s ease-out;
          }
          
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(40px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          .animate-scale-in {
            animation: scaleIn 0.5s ease-out;
          }
          
          @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.9); }
            to { opacity: 1; transform: scale(1); }
          }
          
          .animate-slide-in-left {
            animation: slideInLeft 0.6s ease-out;
          }
          
          @keyframes slideInLeft {
            from { opacity: 0; transform: translateX(-30px); }
            to { opacity: 1; transform: translateX(0); }
          }
          
          .animate-slide-in-right {
            animation: slideInRight 0.6s ease-out;
          }
          
          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(30px); }
            to { opacity: 1; transform: translateX(0); }
          }
          
          .gradient-text {
            background: linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
          
          .animate-slide-out {
            animation: slideOut 0.3s ease-in forwards;
          }
          
          @keyframes slideOut {
            to { opacity: 0; transform: translateX(-30px); }
          }
        `}</style>
        
        <div className="max-w-2xl w-full text-center animate-fade-in">
          <div className="-mb-20 flex justify-center animate-scale-in">
            <img src="/logo-full.svg" alt="VibeShelf" className="h-80 w-auto" />
          </div>
          
          <p className={`text-2xl ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 font-sans font-semibold animate-fade-in-up`} style={{animationDelay: '0.2s', opacity: 0, animationFillMode: 'forwards'}}>
            Find Books That Match Your Vibe
          </p>

          <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-12 font-sans max-w-xl mx-auto leading-relaxed animate-fade-in-up`} style={{animationDelay: '0.4s', opacity: 0, animationFillMode: 'forwards'}}>
            Answer 10 carefully crafted questions and we'll recommend a book that matches your exact mood and preferences.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{animationDelay: '0.6s', opacity: 0, animationFillMode: 'forwards'}}>
            <button
              onClick={() => {
                ReactGA.event({ category: "Quiz", action: "Started Quiz" });
                setStage('quiz');
              }}
              className="bg-orange-600 text-white px-10 py-4 rounded-full font-sans font-semibold text-lg hover:bg-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center justify-center gap-3"
            >
              Start the Journey
              <ChevronRight className="w-5 h-5" />
            </button>
            
            <button
              onClick={surpriseMe}
              className={`${darkMode ? 'bg-gray-800 text-orange-400 border-orange-400 hover:bg-gray-700' : 'bg-white text-orange-600 border-orange-600 hover:bg-orange-50'} border-2 px-10 py-4 rounded-full font-sans font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-3`}
            >
              <Award className="w-5 h-5" />
              Surprise Me
            </button>
          </div>

          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-8 font-sans animate-fade-in`} style={{animationDelay: '0.8s', opacity: 0, animationFillMode: 'forwards'}}>
            Takes about 2 minutes · Personalized recommendations · {Math.floor(BOOKS.length / 25) * 25}+ curated books
          </p>

          <button
            onClick={() => {
              setStage('browse');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`mt-4 ${darkMode ? 'text-orange-400 hover:text-orange-300' : 'text-orange-600 hover:text-orange-700'} font-sans font-semibold transition-colors underline animate-fade-in`}
            style={{animationDelay: '1s', opacity: 0, animationFillMode: 'forwards'}}
          >
            Or browse all books
          </button>
        </div>
      </div>
    );
  }

  // Quiz Screen
  if (stage === 'quiz') {
    const question = QUESTIONS[currentQuestion];
    const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;

    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800' : 'bg-gradient-to-br from-amber-50 via-orange-50 to-red-50'} flex flex-col items-center justify-start pt-10 p-4`}>
        <div className="max-w-3xl w-full">
          <div className="mb-12">
            <div className="flex justify-between items-center mb-3">
              <span className={`text-sm font-sans font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Question {currentQuestion + 1} of {QUESTIONS.length}
              </span>
              <span className={`text-sm font-sans font-medium ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                {Math.round(progress)}%
              </span>
            </div>
            <div className={`h-2 ${darkMode ? 'bg-gray-700' : 'bg-orange-100'} rounded-full overflow-hidden`}>
              <div 
                className="h-full bg-orange-600 transition-all duration-500 ease-out rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          
          <div className={`${isAnimating ? 'animate-slide-out' : 'animate-fade-in'}`}>
            <h2 className={`text-5xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-3 font-serif leading-tight`}>
              {question.question}
            </h2>
            <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-10 font-sans`}>
              {question.subtitle}
            </p>

            <div className="space-y-4">
              {question.answers.map((answer, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAnswer(answer.scores, question.weight || 1, answer.text, answer.pageRange, answer.yearRange)}
                  className={`w-full text-left p-6 ${darkMode ? 'bg-gray-800 border-gray-700 hover:border-orange-500' : 'bg-white border-gray-200 hover:border-orange-500'} rounded-2xl border-2 hover:shadow-xl transition-all duration-300 group`}
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xl font-sans font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'} group-hover:text-orange-500 transition-colors`}>
                      {answer.text}
                    </span>
                    <ChevronRight className={`w-6 h-6 ${darkMode ? 'text-gray-500' : 'text-gray-400'} group-hover:text-orange-500 group-hover:translate-x-2 transition-all`} />
                  </div>
                </button>
              ))}
            </div>
          </div>

            {/* GO BACK BUTTON */}
            <button
              onClick={() => {
                if (currentQuestion === 0) {
                  setStage('intro');
                } else {
                  goBack();
                }
              }}
              className={`mt-6 ${darkMode ? 'bg-gray-800 text-orange-400 border-orange-400 hover:bg-gray-700' : 'bg-white text-orange-600 border-orange-600 hover:bg-orange-50'} border-2 px-8 py-3 rounded-full font-sans font-semibold transition-all duration-300 inline-flex items-center gap-2`}
            >
              <ChevronLeft className="w-5 h-5" />
              {currentQuestion === 0 ? 'Back to Home' : 'Go Back to Previous Question'}
            </button>
        </div>
      </div>
    );
  }

  // Result Screen
  if (stage === 'result' && recommendedBook) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800' : 'bg-gradient-to-br from-amber-50 via-orange-50 to-red-50'} py-12 px-4`}>
        {showWishlist && <WishlistModal />}
        {showReadList && <ReadListModal />}
        {showReadConfirmModal && <ReadConfirmModal />}
        <MobileMenuDropdown />

        {/* Home Button */}
        <button
          onClick={() => {
            setStage('intro');
            setSearchQuery('');
            setExpandedBook(null);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className={`fixed top-6 left-6 bg-white text-orange-600 border-2 border-orange-600 px-3 sm:px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-40 inline-flex items-center gap-2 font-sans font-semibold ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          title="Back to Home"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="hidden sm:inline">Home</span>
        </button>

        {/* Mobile Menu Button - visible only on mobile */}
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          className={`fixed top-6 right-6 sm:hidden bg-white text-gray-700 border-2 border-gray-300 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-40 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          title="Menu"
        >
          {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          {(wishlist.length > 0 || readList.length > 0) && !showMobileMenu && (
            <span className="absolute -top-1 -right-1 bg-orange-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-sans font-bold">
              {wishlist.length + readList.length}
            </span>
          )}
        </button>

        {/* Wishlist Button - hidden on mobile, visible on sm+ */}
        <button
          onClick={() => setShowWishlist(true)}
          className={`fixed top-6 right-6 hidden sm:block bg-white text-orange-600 border-2 border-orange-600 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-40 group ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          title="View Reading List"
        >
          <div className="relative">
            <Bookmark className="w-6 h-6" />
            {wishlist.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-orange-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-sans font-bold">
                {wishlist.length}
              </span>
            )}
          </div>
        </button>

        {/* Read List Button - hidden on mobile, visible on sm+ */}
        <button
          onClick={() => setShowReadList(true)}
          className={`fixed top-6 right-20 hidden sm:block bg-white text-green-600 border-2 border-green-500 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-40 group ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          title="View Books I've Read"
        >
          <div className="relative">
            <BookCheck className="w-6 h-6" />
            {readList.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-sans font-bold">
                {readList.length}
              </span>
            )}
          </div>
        </button>

        <div className="max-w-6xl mx-auto animate-fade-in">
          {/* Main Recommendation */}
          <div className="text-center mb-4 sm:mb-8">
            <h2 className="text-lg sm:text-2xl font-sans font-semibold text-orange-600 mb-1 sm:mb-2">
              Your Perfect Match
            </h2>
            <h1 className={`text-3xl sm:text-5xl md:text-6xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} font-serif leading-tight`}>
              {recommendedBook.title}
            </h1>
            <p className={`text-lg sm:text-2xl ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-1 sm:mt-2 font-serif italic`}>
              by {recommendedBook.author}
            </p>

            {/* Why This Book Explanation */}
            <p className={`text-sm sm:text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-2 sm:mt-4 font-sans max-w-2xl mx-auto`}>
              {generateExplanation(scores, recommendedBook)}
            </p>

            {/* Share Button */}
            <div className="mt-3 sm:mt-6">
              <button
                onClick={handleShare}
                className={`${darkMode ? 'bg-gray-800 text-orange-400 border-orange-400 hover:bg-gray-700' : 'bg-white text-orange-600 border-orange-600 hover:bg-orange-50'} border-2 px-4 sm:px-6 py-2 sm:py-3 rounded-full font-sans font-semibold text-sm sm:text-base transition-all duration-300 inline-flex items-center gap-2 shadow-lg hover:shadow-xl`}
              >
                {shareSuccess ? (
                  <>
                    <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                    Copied to Clipboard!
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    Share Your Match
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="mb-16">
            <BookCard book={recommendedBook} isExpanded={false} isFeatured={true} />
          </div>
          
          {/* Similar Books Section */}
          <div className="mb-8">
            <h2 className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} font-serif text-center mb-4`}>
              More Books You Might Love
            </h2>
            <p className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'} font-sans text-lg mb-4`}>
              Based on your preferences and this recommendation
            </p>

            {/* Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              <button
                onClick={() => setSimilarBooksFilter('default')}
                className={`px-4 py-2 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                  similarBooksFilter === 'default'
                    ? 'bg-orange-600 text-white shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-orange-500 hover:text-orange-400' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-600 hover:text-orange-600'
                }`}
              >
                Best Match
              </button>
              <button
                onClick={() => setSimilarBooksFilter('rating')}
                className={`px-4 py-2 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                  similarBooksFilter === 'rating'
                    ? 'bg-orange-600 text-white shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-orange-500 hover:text-orange-400' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-600 hover:text-orange-600'
                }`}
              >
                Highest Rated
              </button>
              <button
                onClick={() => setSimilarBooksFilter('year-new')}
                className={`px-4 py-2 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                  similarBooksFilter === 'year-new'
                    ? 'bg-orange-600 text-white shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-orange-500 hover:text-orange-400' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-600 hover:text-orange-600'
                }`}
              >
                Newest First
              </button>
              <button
                onClick={() => setSimilarBooksFilter('year-old')}
                className={`px-4 py-2 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                  similarBooksFilter === 'year-old'
                    ? 'bg-orange-600 text-white shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-orange-500 hover:text-orange-400' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-600 hover:text-orange-600'
                }`}
              >
                Oldest First
              </button>
              <button
                onClick={() => setSimilarBooksFilter('pages-short')}
                className={`px-4 py-2 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                  similarBooksFilter === 'pages-short'
                    ? 'bg-orange-600 text-white shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-orange-500 hover:text-orange-400' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-600 hover:text-orange-600'
                }`}
              >
                Shortest
              </button>
              <button
                onClick={() => setSimilarBooksFilter('pages-long')}
                className={`px-4 py-2 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                  similarBooksFilter === 'pages-long'
                    ? 'bg-orange-600 text-white shadow-md'
                    : darkMode ? 'bg-gray-800 text-gray-300 border border-gray-600 hover:border-orange-500 hover:text-orange-400' : 'bg-white text-gray-600 border border-gray-300 hover:border-orange-600 hover:text-orange-600'
                }`}
              >
                Longest
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" style={{ gridAutoRows: expandedBook ? 'auto' : '1fr' }}>
            {getFilteredSimilarBooks().map(book => {
              if (!cardRefs.current[book.id]) {
                cardRefs.current[book.id] = React.createRef();
              }
              return (
                <BookCard
                  key={book.id}
                  book={book}
                  isExpanded={expandedBook === book.id}
                  cardRef={cardRefs.current[book.id]}
                  onToggle={() => {
                    const newExpandedState = expandedBook === book.id ? null : book.id;
                    setExpandedBook(newExpandedState);

                    // If expanding (not closing), scroll to center the card
                    if (newExpandedState === book.id) {
                      setTimeout(() => {
                        const cardElement = cardRefs.current[book.id]?.current;
                        if (cardElement) {
                          const rect = cardElement.getBoundingClientRect();
                          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                          const cardTop = rect.top + scrollTop;
                          const windowHeight = window.innerHeight;
                          const cardHeight = rect.height;
                          const targetScroll = cardTop - (windowHeight / 2) + (cardHeight / 2);

                          window.scrollTo({
                            top: Math.max(0, targetScroll),
                            behavior: 'smooth'
                          });
                        }
                      }, 100);
                    }
                  }}
                />
              );
            })}
          </div>
          
          {/* Action Buttons */}
          <div className="text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <button
                onClick={surpriseMe}
                className="bg-white text-orange-600 border-2 border-orange-600 px-8 py-4 rounded-full font-sans font-semibold text-lg hover:bg-orange-50 transition-all duration-300 inline-flex items-center justify-center gap-3 shadow-lg"
              >
                <Award className="w-5 h-5" />
                Surprise Me Again
              </button>

              <button
                onClick={reset}
                className="bg-orange-600 text-white px-8 py-4 rounded-full font-sans font-semibold text-lg hover:bg-orange-700 transition-all duration-300 inline-flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <RotateCcw className="w-5 h-5" />
                Start Over & Take Quiz
              </button>
            </div>

            <button
              onClick={() => {
                setStage('browse');
                setExpandedBook(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="mt-4 text-orange-600 font-sans font-semibold hover:text-orange-700 transition-colors underline"
            >
              Browse all books
            </button>

            <p className="text-sm text-gray-500 font-sans mt-4">
              Powered by VibeShelf · We may earn from qualifying purchases
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}